import { useEffect, useRef, useState, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, DirectionsRenderer } from '@react-google-maps/api';
import { MapPin, Navigation, Loader2 } from 'lucide-react';
import { Input } from './ui/input';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const libraries = ['places'];

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '12px',
};

const defaultCenter = {
  lat: 43.7102,
  lng: 7.2620, // Nice, France
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  styles: [
    { elementType: 'geometry', stylers: [{ color: '#1d1d1d' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#1d1d1d' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
    {
      featureType: 'administrative.locality',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#d59563' }],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#d59563' }],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [{ color: '#263c3f' }],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#6b9a76' }],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [{ color: '#38414e' }],
    },
    {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [{ color: '#212a37' }],
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#9ca5b3' }],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [{ color: '#746855' }],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [{ color: '#1f2835' }],
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#f3d19c' }],
    },
    {
      featureType: 'transit',
      elementType: 'geometry',
      stylers: [{ color: '#2f3948' }],
    },
    {
      featureType: 'transit.station',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#d59563' }],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#17263c' }],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#515c6d' }],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.stroke',
      stylers: [{ color: '#17263c' }],
    },
  ],
};

// Places Autocomplete Input Component
export const GooglePlacesAutocomplete = ({ 
  placeholder, 
  value, 
  onSelect, 
  icon: Icon = MapPin,
  className = '' 
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    if (inputRef.current && value) {
      inputRef.current.value = value;
    }
  }, [value]);

  useEffect(() => {
    if (!isLoaded || !inputRef.current) return;

    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: ['fr', 'mc'] },
      fields: ['formatted_address', 'geometry', 'name', 'place_id'],
      types: ['establishment', 'geocode'],
      bounds: new window.google.maps.LatLngBounds(
        { lat: 43.45, lng: 6.5 },
        { lat: 43.85, lng: 7.55 }
      ),
      strictBounds: false,
    });

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        const result = {
          display_name: place.formatted_address || place.name,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          place_id: place.place_id,
        };
        if (inputRef.current) inputRef.current.value = result.display_name;
        onSelectRef.current(result);
      }
    });

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isLoaded]);

  if (!isLoaded) {
    return (
      <div className={`relative ${className}`}>
        <div className="absolute left-3 top-1/2 -translate-y-1/2">
          <Loader2 className="w-4 h-4 text-zinc-500 animate-spin" />
        </div>
        <Input
          disabled
          placeholder="Chargement Google Maps..."
          className="pl-10 bg-zinc-800 border-zinc-700 text-white"
        />
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
        <Icon className="w-4 h-4 text-emerald-500" />
      </div>
      <input
        ref={inputRef}
        type="text"
        defaultValue={value || ''}
        placeholder={placeholder}
        className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
        data-testid="google-places-input"
      />
    </div>
  );
};

// Map Component with route display and optional interactive pin placement
export const GoogleMapDisplay = ({ 
  pickupCoords, 
  destCoords, 
  height = '300px',
  showRoute = true,
  interactive = false,
  pinCoords = null,
  onPinPlace = null,
}) => {
  const [directions, setDirections] = useState(null);
  const [map, setMap] = useState(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Handle map click for pin placement
  const handleMapClick = useCallback((e) => {
    if (!interactive || !onPinPlace) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    // Reverse geocode
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      const address = status === 'OK' && results[0] ? results[0].formatted_address : `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      onPinPlace({ lat, lng, address });
    });
  }, [interactive, onPinPlace]);

  // Calculate route when both coordinates are available
  useEffect(() => {
    const origin = pinCoords || pickupCoords;
    if (!isLoaded || !origin || !destCoords || !showRoute) {
      setDirections(null);
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: { lat: origin.lat, lng: origin.lng },
        destination: { lat: destCoords.lat, lng: destCoords.lng },
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === 'OK') {
          setDirections(result);
        } else {
          console.error('Directions request failed:', status);
        }
      }
    );
  }, [isLoaded, pickupCoords, destCoords, showRoute, pinCoords]);

  // Fit bounds when markers change
  useEffect(() => {
    if (!map || !isLoaded) return;
    const origin = pinCoords || pickupCoords;

    if (origin && destCoords) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({ lat: origin.lat, lng: origin.lng });
      bounds.extend({ lat: destCoords.lat, lng: destCoords.lng });
      map.fitBounds(bounds, { padding: 50 });
    } else if (origin) {
      map.panTo({ lat: origin.lat, lng: origin.lng });
      map.setZoom(15);
    } else if (destCoords) {
      map.panTo({ lat: destCoords.lat, lng: destCoords.lng });
      map.setZoom(14);
    }
  }, [map, isLoaded, pickupCoords, destCoords, pinCoords]);

  if (!isLoaded) {
    return (
      <div 
        className="bg-zinc-800 rounded-xl flex items-center justify-center"
        style={{ height }}
      >
        <div className="flex items-center gap-2 text-zinc-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Chargement de la carte...</span>
        </div>
      </div>
    );
  }

  const effectivePickup = pinCoords || pickupCoords;

  return (
    <div style={{ height }} className="rounded-xl overflow-hidden border border-zinc-700 relative">
      {interactive && (
        <div className="absolute top-2 left-2 z-10 bg-black/70 backdrop-blur-sm text-emerald-400 text-xs px-3 py-1.5 rounded-full border border-emerald-500/30" data-testid="pin-mode-indicator">
          Cliquez sur la carte pour placer le repere
        </div>
      )}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={effectivePickup || destCoords || defaultCenter}
        zoom={12}
        options={{...mapOptions, draggableCursor: interactive ? 'crosshair' : undefined}}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={handleMapClick}
      >
        {/* Pin marker (draggable) */}
        {pinCoords && (
          <Marker
            position={{ lat: pinCoords.lat, lng: pinCoords.lng }}
            draggable={interactive}
            onDragEnd={(e) => {
              if (!onPinPlace) return;
              const lat = e.latLng.lat();
              const lng = e.latLng.lng();
              const geocoder = new window.google.maps.Geocoder();
              geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                const address = status === 'OK' && results[0] ? results[0].formatted_address : `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
                onPinPlace({ lat, lng, address });
              });
            }}
            icon={{
              path: 'M12 0C7.31 0 3.5 3.81 3.5 8.5C3.5 14.88 12 24 12 24S20.5 14.88 20.5 8.5C20.5 3.81 16.69 0 12 0ZM12 11.5C10.34 11.5 9 10.16 9 8.5S10.34 5.5 12 5.5S15 6.84 15 8.5S13.66 11.5 12 11.5Z',
              fillColor: '#10b981',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
              scale: 1.5,
              anchor: new window.google.maps.Point(12, 24),
            }}
          />
        )}

        {/* Pickup Marker (only if no pin and no directions) */}
        {pickupCoords && !pinCoords && !directions && (
          <Marker
            position={{ lat: pickupCoords.lat, lng: pickupCoords.lng }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#10b981',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            }}
          />
        )}

        {/* Destination Marker */}
        {destCoords && !directions && (
          <Marker
            position={{ lat: destCoords.lat, lng: destCoords.lng }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#ef4444',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            }}
          />
        )}

        {/* Route */}
        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              polylineOptions: {
                strokeColor: '#10b981',
                strokeWeight: 4,
                strokeOpacity: 0.8,
              },
              suppressMarkers: false,
              markerOptions: {
                origin: {
                  icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 8,
                    fillColor: '#10b981',
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeWeight: 2,
                  },
                },
                destination: {
                  icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 8,
                    fillColor: '#ef4444',
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeWeight: 2,
                  },
                },
              },
            }}
          />
        )}
      </GoogleMap>
    </div>
  );
};

// Export a simple hook to check if Google Maps is loaded
export const useGoogleMaps = () => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  return { isLoaded, loadError };
};
